import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { authService } from '../services/authService';
import { LoadingPage } from '../pages/LoadingPage';
import { RegistrationPage } from '../pages/RegistrationPage';
import { Navigation } from './Navigation';
import { LanguageSelector } from './LanguageSelector';
import { getGlobalBootstrap } from '../utils/appBootstrap';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { state } = useApp();
  const { t, ready } = useTranslation();
  const location = useLocation();

  // Clean up impersonation when page becomes hidden or user leaves
  useEffect(() => {
    const cleanupImpersonation = () => {
      const impersonationData = sessionStorage.getItem('impersonationData');
      if (impersonationData) {
        console.log('🛡️ Layout: App losing focus - cleaning up impersonation');
        const adminBackupToken = sessionStorage.getItem('adminBackupToken');
        
        if (adminBackupToken) {
          // Restore admin token
          authService.setToken(adminBackupToken);
          console.log('🛡️ Layout: Restored admin token from backup');
        } else {
          // Clear token if no backup
          authService.clearToken();
          console.log('🛡️ Layout: Cleared auth token - no backup available');
        }
        
        // Clear impersonation data
        sessionStorage.removeItem('impersonationData');
        sessionStorage.removeItem('adminBackupToken');
      }
    };

    // Handle page visibility change
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('🛡️ Layout: Page became hidden');
        cleanupImpersonation();
      }
    };

    // Handle page unload
    const handleBeforeUnload = () => {
      console.log('🛡️ Layout: Page unloading');
      cleanupImpersonation();
    };

    // Handle page blur (when user leaves the tab/window)
    const handleWindowBlur = () => {
      console.log('🛡️ Layout: Window lost focus');
      cleanupImpersonation();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, []);

  // Wait for i18n to be ready to prevent showing translation keys
  if (!ready) {
    return <LoadingPage />;
  }

  if (state.isLoading) {
    return <LoadingPage />;
  }

  if (state.error) {
    return (
      <div className="modern-error-layout">
        <div className="modern-error-card">
          <LanguageSelector className="language-selector-top" />
          <div className="error-content">
            <div className="error-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <h2 className="error-title">{t('common.error')}</h2>
            <p className="error-message">{state.error}</p>
            <button 
              className="btn-modern btn-primary-modern"
              onClick={() => window.location.reload()}
            >
              {t('common.refresh')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Allow specific routes to bypass authentication checks
  const authBypassRoutes = ['/register-password', '/auth'];
  const shouldBypassAuth = authBypassRoutes.includes(location.pathname);
  const bootstrap = getGlobalBootstrap();

  // If not authenticated, check bootstrap environment before deciding what to show
  if (!state.isAuthenticated && !shouldBypassAuth) {
    // For browser environment, let normal routing handle it (user will be redirected to /auth)
    if (bootstrap?.environment === 'browser') {
      // Don't show RegistrationPage, let the routing system handle browser users
      return <>{children}</>;
    } else {
      // For Telegram environment, show RegistrationPage
      return <RegistrationPage />;
    }
  }

  // If user needs registration (new user from backend) - but allow admins to bypass and allow auth bypass routes
  if ((state.user?.status === -1 || state.user?.needsRegistration) && state.user?.role !== 2 && !shouldBypassAuth) { // -1 = new, 2 = admin
    return <RegistrationPage />;
  }

  // If user is pending approval - but allow admins to bypass and allow auth bypass routes
  if (state.user?.status === 0 && state.user?.role !== 2 && !shouldBypassAuth) { // 0 = pending, 2 = admin
    return (
      <div className="modern-pending-approval">
        <div className="modern-pending-card">
          <LanguageSelector className="language-selector-top" />
          
          <div className="pending-header">
            <div className="pending-icon">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12,6 12,12 16,14"/>
              </svg>
            </div>
            <h2 className="pending-title">{t('auth.pending.title')}</h2>
            <p className="pending-message">
              {t('auth.pending.message')}
            </p>
          </div>

          <div className="pending-status-card">
            <div className="status-indicator">
              <div className="status-dot pending"></div>
              <span className="status-text">{t('auth.pending.statusReview')}</span>
            </div>
            <p className="status-description">
              {t('auth.pending.statusDescription')}
            </p>
          </div>

          <div className="pending-user-info">
            <h3 className="info-title">Application Details</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">{t('auth.registration.fullName')}</span>
                <span className="info-value">{state.user.fullName}</span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('auth.registration.phone')}</span>
                <span className="info-value">
                  <a 
                    href={`tel:${state.user.phoneE164.value.replace(/[^\d+]/g, '')}`}
                    className="phone-link subtle"
                    title={`Call ${state.user.fullName}`}
                  >
                    {state.user.phoneE164.value}
                  </a>
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">{t('auth.registration.licensePlate')}</span>
                <span className="info-value">{state.user.primaryPlate.value}</span>
              </div>
            </div>
          </div>

          <div className="pending-timeline">
            <div className="timeline-item completed">
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <h4>Registration Submitted</h4>
                <p>Your application has been received</p>
              </div>
            </div>
            <div className="timeline-item active">
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <h4>{t('auth.pending.timelineReview')}</h4>
                <p>{t('auth.pending.timelineReviewDesc')}</p>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <h4>{t('auth.pending.timelineAccess')}</h4>
                <p>{t('auth.pending.timelineAccessDesc')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If user is rejected (but allow auth bypass routes)
  if (state.user?.status === 2 && !shouldBypassAuth) { // 2 = rejected
    return (
      <div className="modern-user-rejected">
        <div className="modern-rejection-card">
          <LanguageSelector className="language-selector-top" />
          
          <div className="rejection-header">
            <div className="rejection-icon">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            </div>
            <h2 className="rejection-title">{t('auth.registration.rejected')}</h2>
            <p className="rejection-message">
              {t('auth.registration.rejectedMessage')}
            </p>
          </div>

          <div className="rejection-actions">
            <button 
              className="btn-modern btn-secondary-modern"
              onClick={() => window.location.reload()}
            >
              {t('auth.registration.contactSupport')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Normal authenticated layout
  return (
    <div className="app-layout">
      <Navigation />
      <main className="main-content">
        {children}
      </main>
    </div>
  );
}